import { useStacklineTheme } from '@stackline/ui';
import React, { useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import {
  useApplyEntityFilters,
  useRemoveRetailPriceRangeFilters
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import { RatingsAndReviewMetric } from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewMetricTypes';
import {
  childLifetimeRatingVolumeByStarResponse,
  parseChildLifetimeRatingVolumeByStarPercentResponse,
  parseChildLifetimeRatingVolumeByStarResponse,
  useLatestCumulativeReviewWeekId
} from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewsHooks';
import { useProductEntity } from 'src/utils/Hooks/reduxSelectors';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { GenericXAxisLabel } from 'src/components/BeaconV2Components/TopEntityChartV2/GenericXAxisLabel';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import TopEntityChartV2Loading from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2Loading';
import {
  refineSubtitle,
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import AdvancedSearchRequestBuilder from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import AggregationBuilder from 'src/components/BeaconV2Components/utils/AggregationBuilder';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';
import { useAppSelector } from 'src/utils/Hooks';
import useGenericAdvancedSearch from 'src/utils/Hooks/useGenericAdvancedSearch';
import { saveSingleBarChartAsDelimitedData } from 'src/utils/chartUtils';
import { METRICTYPE } from 'src/utils/entityDefinitions';

const lifetimeReviewVolumeOption = [
  { label: 'Cumulative Review Volume', id: RatingsAndReviewMetric.CUMULATIVE_REVIEW_VOLUME },
  { label: 'Cumulative Review Volume %', id: 'cumulativeReviewVolumePercent' }
];

export const useLifetimeReviewVolumeByStarData = () => {
  const retailer = useAppSelector((state) => state.retailer);
  const beaconClientId = useAppSelector((state) => state.user.config.vendor.BeaconClientId);
  const { latestCumulativeWeekId } = useLatestCumulativeReviewWeekId();

  const applyEntityFilters = useApplyEntityFilters();
  const removeRetailPriceRangeFilters = useRemoveRetailPriceRangeFilters('reviews');

  const aggregationBychildLifetimeRatingVolume = new AggregationBuilder('retailerId')
    .addAggregationField('Life Time One Star Review Volume', 'lifetimeOneStarReviewVolume', 'sum', false)

    .addAggregationField('Life Time Two Star Review Volume', 'lifetimeTwoStarsReviewVolume', 'sum', false)

    .addAggregationField('Life Time Three Star Review Volume', 'lifetimeThreeStarsReviewVolume', 'sum', false)

    .addAggregationField('Life Time Four Star Review Volume', 'lifetimeFourStarsReviewVolume', 'sum', false)

    .addAggregationField('Life Time Five Star Review Volume', 'lifetimeFiveStarsReviewVolume', 'sum', false)
    .setSortDirection(null)
    .setGroupByFieldName('retailerId')
    .build();

  const requestBychildLifetimeRatingVolume = new AdvancedSearchRequestBuilder(
    `mainEntityMetrics-client-${beaconClientId}-lifeTimeReviewVolumeByStar`,
    'beacon-ratings-reviews-metrics-v2'
  )
    .setPageNumber(1)
    .setPageSize(10000)
    .setRetailerId(retailer.id)
    .setPeriod('year')
    .setDoAggregation(true)
    .setReturnDocuments(false)
    .addConditionRangeFilter('weekId', latestCumulativeWeekId, latestCumulativeWeekId)
    .addConditionTermFilter('retailerId', 'should', [retailer.id])
    .setSearchBy('parent')
    .addAggregation(aggregationBychildLifetimeRatingVolume)
    .addSortField('Life Time Review Volume', 'lifetimeReviewVolume', 'sum', false)
    .apply(applyEntityFilters)
    .apply(removeRetailPriceRangeFilters);

  const request = requestBychildLifetimeRatingVolume.build();

  const { isLoading, data } = useGenericAdvancedSearch({
    requestId: 'lifetimeReviewVolumeByStar-columnChart',
    requestBody: [request],
    queryKeys: [request]
  });

  return { isLoading, data };
};

/**
 *  Top Entity Chart for Reviews Cumulative Review Volume / Volume % by star rating.
 * */

const LifetimeReviewVolumeByStarRatingChart = () => {
  const { data, isLoading } = useLifetimeReviewVolumeByStarData();
  const productEntity = useProductEntity();
  const fieldLevel = productEntity ? 'parent' : 'child';

  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();
  const [selectedMetric, setSelectedMetric] = useState({
    label: 'Cumulative Review Volume',
    id: RatingsAndReviewMetric.CUMULATIVE_REVIEW_VOLUME
  });
  const legendLabel = useAppSelector((state) => state.mainTimePeriod.displayName);
  const theme = useStacklineTheme();
  const chartData = data
    ? selectedMetric.id === RatingsAndReviewMetric.CUMULATIVE_REVIEW_VOLUME
      ? parseChildLifetimeRatingVolumeByStarResponse({
          data: (data as unknown as { data: childLifetimeRatingVolumeByStarResponse }).data,
          fieldLevel
        })
      : parseChildLifetimeRatingVolumeByStarPercentResponse({
          data: (data as unknown as { data: childLifetimeRatingVolumeByStarResponse }).data,
          fieldLevel
        })
    : [];

  const subtitle = refineSubtitle([entityPhrase, taxonomyText]);

  return (
    <BeaconChartWithLegend
      convertSeriesToDelimitedData={async () => saveSingleBarChartAsDelimitedData(chartData)}
      includeSummaryInfo={false}
      loading={isLoading}
      loadingComponent={<TopEntityChartV2Loading enableSubTitleSkeleton />}
      subtitle={
        <BeaconSubtitleContainer>
          <Text variant="body1">
            {`The chart displays the ${selectedMetric.label.toLowerCase()} grouped by star rating for `}
            {subtitle}.
            <SubtitleFilters />
          </Text>
        </BeaconSubtitleContainer>
      }
      singleDropdownProps={{
        defaultLabel: '',
        options: lifetimeReviewVolumeOption,
        selectedId: selectedMetric.id,
        onChange: (option) => setSelectedMetric(option),
        suffix: 'by Star Rating'
      }}
      primaryLegendProps={{
        displayName: legendLabel
      }}
    >
      <TopEntityChartV2
        xAxisLabelTextStyles={{ lineHeight: 1.6 }}
        xAxisFormatter={(xAxisCategory) => {
          return ReactDOMServer.renderToStaticMarkup(
            <GenericXAxisLabel xAxisCategory={xAxisCategory} wrapperStyle={{ width: '50px' }} />
          );
        }}
        chartSeries={[
          {
            type: 'column',
            data: chartData,
            metricType:
              selectedMetric.id === RatingsAndReviewMetric.CUMULATIVE_REVIEW_VOLUME
                ? METRICTYPE.VOLUME
                : METRICTYPE.PERCENT
          }
        ]}
        seriesColors={[theme.colors.primary]}
      />
    </BeaconChartWithLegend>
  );
};
export default LifetimeReviewVolumeByStarRatingChart;
